import {
  FunctionComponent,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PercentCrop } from 'react-image-crop';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

import 'react-image-crop/src/ReactCrop.scss';
import classes from './ImageCropModal.module.scss';

export const CROPPABLE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const CROP_OUTPUT_QUALITY = 0.92;

interface GetInitialCropArgs {
  width: number;
  height: number;
  circularCrop?: boolean;
}

interface ImageCropModalProps {
  file: File;
  title: string;
  onCrop: (file: File) => void;
  onClose: () => void;
  circularCrop?: boolean;
  root?: string;
}

const getInitialCrop = ({ width, height, circularCrop }: GetInitialCropArgs): Crop => {
  if (circularCrop) {
    return centerCrop(makeAspectCrop({ unit: '%', width: 50 }, 1, width, height), width, height);
  }

  return { unit: '%', x: 0, y: 0, width: 100, height: 100 };
};

export const ImageCropModal: FunctionComponent<ImageCropModalProps> = memo(
  ({ file, title, onCrop, onClose, circularCrop, root = 'modal-preview-root' }) => {
    const { t } = useTranslation();

    const [imageUrl, setImageUrl] = useState<string>('');

    const [crop, setCrop] = useState<Crop>();

    const [completedCrop, setCompletedCrop] = useState<PercentCrop>();

    const imageRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
      const objectUrl = URL.createObjectURL(file);

      setImageUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }, [file]);

    const onImageLoad = useCallback(
      (event: SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth: width, naturalHeight: height } = event.currentTarget;

        setCrop(getInitialCrop({ width, height, circularCrop }));
      },
      [circularCrop]
    );

    const getCroppedImage = useCallback((): Promise<File> | undefined => {
      const image = imageRef.current;

      if (!image || !completedCrop) {
        return undefined;
      }

      const { naturalWidth, naturalHeight } = image;

      const cropX = (completedCrop.x / 100) * naturalWidth;
      const cropY = (completedCrop.y / 100) * naturalHeight;
      const cropWidth = (completedCrop.width / 100) * naturalWidth;
      const cropHeight = (completedCrop.height / 100) * naturalHeight;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

      canvas.width = Math.round(cropWidth);
      canvas.height = Math.round(cropHeight);

      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

      const mimeType = CROPPABLE_IMAGE_MIME_TYPES.includes(file.type) ? file.type : 'image/png';

      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return;
            }

            resolve(new File([blob], file.name, { type: mimeType }));
          },
          mimeType,
          CROP_OUTPUT_QUALITY
        );
      });
    }, [completedCrop, file]);

    const onCropClick = useCallback(async () => {
      const croppedFile = await getCroppedImage();

      if (!croppedFile) {
        return;
      }

      onCrop(croppedFile);
    }, [getCroppedImage, onCrop]);

    return (
      <Modal
        isOpen
        contentStyle={{ textAlign: 'center' }}
        onClose={onClose}
        ariaLabel={title}
        body={
          <>
            <div className={classes['image-crop-modal__title']}>{title}</div>

            {imageUrl && (
              <ReactCrop
                className={classes['image-crop-modal__crop']}
                crop={crop}
                onChange={setCrop}
                onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
                circularCrop={circularCrop}
                keepSelection
                ruleOfThirds
                {...(circularCrop && {
                  minWidth: 100,
                  minHeight: 100,
                  maxWidth: 500,
                  maxHeight: 500,
                })}
              >
                <img
                  ref={imageRef}
                  src={imageUrl}
                  onLoad={onImageLoad}
                  style={{ maxHeight: 'calc(100dvh - 15rem)' }}
                  alt={''}
                />
              </ReactCrop>
            )}

            <div className={classes['image-crop-modal__crop-buttons']}>
              <Button label={t('common.cancel')} type={ButtonType.secondary} onClick={onClose} />
              <Button
                label={t('imageCrop.crop-and-save')}
                type={ButtonType.primary}
                onClick={onCropClick}
                disabled={!completedCrop}
              />
            </div>
          </>
        }
        root={root}
      />
    );
  }
);
